import React, { useEffect, useId, useRef, useState } from 'react';
import { motion } from 'framer-motion';

export function AnimatedBeam({
    containerRef,
    fromRef,
    toRef,
    curvature = 0,
    reverse = false,
    duration = null,
    delay = 0,
    pathColor = 'gray',
    pathWidth = 2,
    pathOpacity = 0.2,
    gradientStartColor = '#ffaa40',
    gradientStopColor = '#9c40ff',
    startXOffset = 0,
    startYOffset = 0,
    endXOffset = 0,
    endYOffset = 0,
}) {
    const id = useId();
    const stableDuration = useRef(duration ?? (Math.random() * 3 + 4));
    const [pathD, setPathD] = useState('');
    const [svgDimensions, setSvgDimensions] = useState({ width: 0, height: 0 });

    const gradientCoordinates = reverse
        ? {
            x1: ['90%', '-10%'],
            x2: ['100%', '0%'],
            y1: ['0%', '0%'],
            y2: ['0%', '0%'],
        }
        : {
            x1: ['10%', '110%'],
            x2: ['0%', '100%'],
            y1: ['0%', '0%'],
            y2: ['0%', '0%'],
        };

    useEffect(() => {
        const updatePath = () => {
            if (!containerRef?.current || !fromRef?.current || !toRef?.current) return;

            const containerRect = containerRef.current.getBoundingClientRect();
            const rectFrom = fromRef.current.getBoundingClientRect();
            const rectTo = toRef.current.getBoundingClientRect();

            setSvgDimensions({
                width: containerRect.width,
                height: containerRect.height,
            });

            const startX = rectFrom.left - containerRect.left + rectFrom.width / 2 + startXOffset;
            const startY = rectFrom.top - containerRect.top + rectFrom.height / 2 + startYOffset;
            const endX = rectTo.left - containerRect.left + rectTo.width / 2 + endXOffset;
            const endY = rectTo.top - containerRect.top + rectTo.height / 2 + endYOffset;

            const controlY = startY - curvature;
            const d = `M ${startX},${startY} Q ${(startX + endX) / 2},${controlY} ${endX},${endY}`;
            setPathD(d);
        };

        const resizeObserver = new ResizeObserver(updatePath);
        if (containerRef?.current) resizeObserver.observe(containerRef.current);
        updatePath();
        return () => resizeObserver.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <svg
            fill="none"
            width={svgDimensions.width}
            height={svgDimensions.height}
            xmlns="http://www.w3.org/2000/svg"
            className="pointer-events-none absolute left-0 top-0 transform-gpu"
            viewBox={`0 0 ${svgDimensions.width} ${svgDimensions.height}`}
            style={{ zIndex: 0 }}
        >
            <path
                d={pathD}
                stroke={pathColor}
                strokeWidth={pathWidth}
                strokeOpacity={pathOpacity}
                strokeLinecap="round"
            />
            <path
                d={pathD}
                strokeWidth={pathWidth}
                stroke={`url(#${id})`}
                strokeOpacity="1"
                strokeLinecap="round"
            />
            <defs>
                <motion.linearGradient
                    id={id}
                    gradientUnits="userSpaceOnUse"
                    initial={{ x1: '0%', x2: '0%', y1: '0%', y2: '0%' }}
                    animate={{
                        x1: gradientCoordinates.x1,
                        x2: gradientCoordinates.x2,
                        y1: gradientCoordinates.y1,
                        y2: gradientCoordinates.y2,
                    }}
                    transition={{
                        delay,
                        duration: stableDuration.current,
                        ease: [0.16, 1, 0.3, 1],
                        repeat: Infinity,
                        repeatDelay: 0,
                    }}
                >
                    <stop stopColor={gradientStartColor} stopOpacity="0" offset="0%" />
                    <stop stopColor={gradientStartColor} offset="32.5%" />
                    <stop stopColor={gradientStopColor} offset="67.5%" />
                    <stop stopColor={gradientStopColor} stopOpacity="0" offset="100%" />
                </motion.linearGradient>
            </defs>
        </svg>
    );
}
